/**
 * Copyright (c) 2019 2020 2021 2022 AIGIA. All Rights Reserved.
 * see license.txt
 */

import {AfterViewInit, Component, ElementRef, Input, OnChanges, OnInit, ViewChild} from "@angular/core";
import {TranslateService} from "@ngx-translate/core";

interface IRect {
	x: number,
	y: number,
	w: number,
	h: number
}

interface ISize {
	w: number,
	h: number
}

/*
* */
@Component({
	selector: "aigia-score",
	templateUrl: "./score.component.html",
	styleUrls: ["./score.component.css"]
})
export class ScoreComponent implements OnChanges, OnInit, AfterViewInit {
	@Input() public size: number[];
	@Input() public shot: any;
	@Input() public type: string;
	@Input() public subtype: string = "スコア";

	public subtypes: string[] = ["スコア"];

	@ViewChild("score", {read: ElementRef}) private canvas_ref: ElementRef;
	private canvas: any;
	private context: any;
	private resizedFrame: IRect;

	constructor(
		public translate: TranslateService
	) {

	}

	/**
	 *
	 */


	/**
	 *
	 */

	public score(shot: any): number {
		let result: number = 0;
		if (shot.platform) {
			if (shot.platform.description) {
				if (shot.platform.description.score) {
					result = Math.floor(shot.platform.description.score);
				}
			}
		}
		return result;
	}


	public ngOnInit(): void {
	}

	public ngOnChanges(changes: any): void {
	}

	public ngAfterViewInit(): void {
		this.canvas = this.canvas_ref.nativeElement;
		this.canvas.width = this.size[0];
		this.canvas.height = this.size[1];
		this.context = this.canvas.getContext("2d");
		this.resizedFrame = this.applyResizingBehavior(this.makeRect(0, 0, 100, 100), {x: 0, y: 0, h: this.canvas.height, w: this.canvas.width});

		this.initializeCanvas();
		this.context.translate(this.resizedFrame.x, this.resizedFrame.y);
		this.context.scale(this.resizedFrame.w / 100, this.resizedFrame.h / 100);

		let score: number;
		switch (this.type) {
			default:
				score = this.score(this.shot);
				break;
		}

		this.draw(score);
	}

	public changeSubtype(): void {
		let score: number;
		switch (this.type) {
			default:
				score = this.score(this.shot);
				break;
		}

		this.clearCanvas();
		this.draw(score);
	}

	public draw(value: number): void {

		this.context.save();

		const color: string = "rgba(50, 221, 221, 1)";

		const point: number = value / 100 * 360;
		const value_text: string = ("" + Math.floor(value));

		this.context.save();
		this.context.translate(50.5, 51.5);
		this.context.rotate(-90 * Math.PI / 180);

		this.arc(-40, -40, 80, 80, -(point - 0.025), 0);
		this.context.fillStyle = "rgba(0, 0, 0, 0)";
		this.context.fill();
		this.context.strokeStyle = color;
		this.context.lineWidth = 5;
		this.context.stroke();

		this.context.restore();

		const textRect: IRect = this.makeRect(22, 35, 58, 32);
		this.context.fillStyle = "rgb(0, 0, 0)";

		this.context.font = "bold 30px HelveticaNeue-Bold, \"Helvetica Neue\", Helvetica, Arial, sans-serif";
		this.context.textAlign = "center";
		const textTotalHeight: number = 30 * 1.3;
		this.context.fillText(value_text, textRect.x + textRect.w / 2, textRect.y + 29 + textRect.h / 2 - textTotalHeight / 2);

		this.context.restore();
	}

	public clearCanvas(): void {
		this.canvas.getContext("2d").clearRect(0, 0, this.canvas.width, this.canvas.height);
	}

	public applyResizingBehavior(rect: IRect, targetRect: IRect): IRect {
		if (targetRect === undefined || this.equalRects(rect, targetRect) || this.equalRects(targetRect, this.makeRect(0, 0, 0, 0))) {
			return rect;
		}

		const scales: ISize = this.makeSize(0, 0);
		scales.w = Math.abs(targetRect.w / rect.w);
		scales.h = Math.abs(targetRect.h / rect.h);

		scales.w = Math.min(scales.w, scales.h);
		scales.h = scales.w;

		const result: IRect = this.makeRect(Math.min(rect.x, rect.x + rect.w), Math.min(rect.y, rect.y + rect.h), Math.abs(rect.w), Math.abs(rect.h));
		result.w *= scales.w;
		result.h *= scales.h;
		result.x = targetRect.x + (targetRect.w - result.w) / 2;
		result.y = targetRect.y + (targetRect.h - result.h) / 2;
		return result;
	}

	public arc(x: number, y: number, w: number, h: number, startAngle: number, endAngle: number): void {
		this.context.save();
		this.context.beginPath();
		this.context.translate(x, y);
		this.context.scale(w / 2, h / 2);
		this.context.arc(1, 1, 1, Math.PI / 180 * startAngle, Math.PI / 180 * endAngle, false);
		this.context.restore();
	}

	public makeRect(x: number, y: number, w: number, h: number): IRect {
		return {x, y, w, h};
	}

	public equalRects(r1: any, r2: any): boolean {
		return r1.x === r2.x && r1.y === r2.y && r1.w === r2.w && r1.h === r2.h;
	}

	public makeSize(w: number, h: number): ISize {
		return {w, h};
	}

	public initializeCanvas(): void {
		const devicePixelRatio: number = window.devicePixelRatio || 1;
		const backingStorePixelRatio: number = this.context.webkitBackingStorePixelRatio
			|| this.context.mozBackingStorePixelRatio
			|| this.context.msBackingStorePixelRatio
			|| this.context.oBackingStorePixelRatio
			|| this.context.backingStorePixelRatio
			|| 1;

		const pixelRatio: number = devicePixelRatio / backingStorePixelRatio;

		this.canvas.style.width = this.canvas.width + "px";
		this.canvas.style.height = this.canvas.height + "px";
		this.canvas.width *= pixelRatio;
		this.canvas.height *= pixelRatio;
		this.canvas.paintCodePixelRatio = pixelRatio;
		this.context.scale(pixelRatio, pixelRatio);
	}

	// 10 + this.size[0] + "px"
	public styleSize(width: string = "fit-content", height: string = "fit-content"): any {
		return {width: width, height: height};
	}
}
